import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, withCtx as _withCtx } from "vue"

import BigNumber from 'bignumber.js'
import { computed } from 'vue'

import Spinner from '@/popup/components/shared/Spinner.vue'
import Tooltip, {
  TooltipPlacement,
} from '@/popup/components/shared/Tooltip/Tooltip.vue'
import { fromTokenUnitWithDecimals } from '@/popup/utils/tokenConversionWithDecimals'


export default /*#__PURE__*/_defineComponent({
  __name: 'FormattedTokenAmount',
  props: {
    decimals: { default: undefined },
    amount: { default: undefined },
    symbol: { default: undefined },
    tooltipOffsetDistance: { default: '5' },
    tooltipPlacement: { default: 'bottom' }
  },
  setup(__props: any) {

const largestNumber = new BigNumber(1e8)
const smallestNumber = new BigNumber(0.000_000_01)

const props = __props

const decodedAmount = computed(() => {
  if (!props.amount) {
    return
  }
  return fromTokenUnitWithDecimals(props.amount, props.decimals)
})

const originalValue = computed(() => decodedAmount.value?.toFixed())

const numberFormatStyle = computed((): BigNumber.Format => {
  const numberFormat = new Intl.NumberFormat(navigator.language)
  const parts = numberFormat.formatToParts(1000.0001)
  const decimalSeparator =
    parts.find(item => item.type === 'decimal')?.value ?? '.'
  const groupSeparator = parts.find(item => item.type === 'group')?.value ?? ','
  return {
    groupSeparator,
    decimalSeparator,
    groupSize: 3,
    fractionGroupSize: 3,
    fractionGroupSeparator: groupSeparator,
  }
})

const formattedValue = computed(() => {
  if (!decodedAmount.value) {
    return
  }

  const value = decodedAmount.value

  if (value.eq(0)) {
    return '0'
  }

  const formatStyle = numberFormatStyle.value

  // Special LYX/LYXt formatting
  if (
    (props.symbol === 'LYX' || props.symbol === 'LYXt') &&
    (value.decimalPlaces() ?? 0) > 2
  ) {
    return value.lt(0.01)
      ? `< 0${formatStyle.decimalSeparator}01`
      : value.toFormat(2, BigNumber.ROUND_DOWN, formatStyle)
  }

  if (value.gt(largestNumber)) {
    // handle large numbers
    return `> ${largestNumber.toFormat(0, formatStyle)}`
  } else if (value.lt(smallestNumber) && value.gt(0)) {
    // handle small numbers
    return `< ${smallestNumber.toFormat(formatStyle)}`
  } else if ((value.decimalPlaces() ?? 0) > 6) {
    const zerosAfterDecimal =
      value.toFixed(18).toString().match('^\\d+(\\.(0+)?)?')?.[2]?.length ?? 0
    // handle numbers with many decimals
    return value.toFormat(
      Math.max(zerosAfterDecimal + 2, 6),
      BigNumber.ROUND_DOWN,
      formatStyle
    )
  } else {
    return value.toFormat(formatStyle)
  }
})

const isLoading = computed(() => !props.amount)

const tooltipActive = computed(() => {
  const { value } = decodedAmount
  if (!value) {
    return false
  }
  return (
    value.gt(largestNumber) ||
    (value.lt(smallestNumber) && value.gt(0)) ||
    (value.decimalPlaces() ?? 0) > 6
  )
})

return (_ctx: any,_cache: any) => {
  return (isLoading.value)
    ? (_openBlock(), _createBlock(Spinner, {
        key: 0,
        "data-testid": "spinner"
      }))
    : (_openBlock(), _createBlock(Tooltip, {
        key: 1,
        placement: _ctx.tooltipPlacement,
        class: "icon-button",
        value: originalValue.value,
        "offset-distance": _ctx.tooltipOffsetDistance,
        hidden: !tooltipActive.value
      }, {
        default: _withCtx(() => [
          _renderSlot(_ctx.$slots, "default", {
            amount: formattedValue.value,
            symbol: _ctx.symbol
          })
        ]),
        _: 3 /* FORWARDED */
      }, 8 /* PROPS */, ["placement", "value", "offset-distance", "hidden"]))
}
}

})